/**
 * Component Name: Footer
 *
 * Description:
 * The Footer component provides site-wide navigation links, company information, contact details, and
 * social media links. It appears at the bottom of every page to support user navigation and enhance
 * site engagement.
 *
 * Usage:
 * - Place the Footer component at the bottom of your application's layout to ensure consistent
 *   visibility across all pages.
 * - The component dynamically filters and displays visible navigation items from the `navItems` array.
 *
 * Key Features:
 * - **Navigation Links:** Displays main site navigation, filtering out hidden items.
 * - **Company Info:** Includes the company logo, description, and contact details.
 * - **Social Media:** Links to the company's social media profiles.
 * - **Dynamic Year:** Automatically updates the copyright year using `getFooterYear()`.
 *
 * Notes:
 * - Social media icons and links are configurable; ensure valid URLs are provided for each platform.
 * - Navigation data is sourced from the `navItems` array, ensuring consistency with the header menu.
 * - CSS customizations are handled in `Footer.styles.scss` for layout and design.
 */

import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getFooterYear } from './Footer.logic';
import './Footer.styles.scss';
import { footerItems } from '../../data/Sitemap'; // Adjust path as necessary
import Icon from '../../components/Icon/Icon';

import logo from '../../assets/images/branding/general-genomics-logo.svg';
import sbagrowth from '../../assets/images/objects/sba-growth-award-2024.svg';
import citi from '../../assets/images/objects/citi.svg';
import sbacert from '../../assets/images/objects/sba-certified.svg';
import socialFacebook from '../../assets/images/social/facebook.svg';
import socialInstagram from '../../assets/images/social/instagram.svg';
import socialLinkedin from '../../assets/images/social/linkedin.svg';
import { useMediaQuery } from '@mui/material';

const Footer: React.FC = () => {
  const currentLocation = useLocation(); // Rename location to currentLocation
  const isMobile = useMediaQuery('(max-width:768px)');

  return (
    <footer className="footer">
      <div className="footer-links">
        <h3>Learn More About General Genomics</h3>
        <div className="footer-nav">
          {footerItems
            .filter((item) => item.nav !== 'hide') // Ignore items with nav: "hide"
            .map((item) => (
              <div key={item.label} className="footer-column">
                {/* Check if the current path matches the item's path */}
                <h3>
                  <Link
                    to={item.path}
                    className={
                      currentLocation.pathname === item.path ? 'current' : ''
                    }
                  >
                    {item.label}
                  </Link>
                </h3>
                <ul>
                  {item.subLinks ? (
                    item.subLinks
                      .filter((subLink) => subLink.nav !== 'hide') // Ignore subLinks with nav: "hide"
                      .map((subLink) => (
                        <li className={subLink.type} key={subLink.label}>
                          {/* Check if the current path matches the subLink's path */}
                          <Link
                            to={subLink.path}
                            className={
                              currentLocation.pathname === subLink.path
                                ? 'current'
                                : ''
                            }
                          >
                            {subLink.label}
                          </Link>
                        </li>
                      ))
                  ) : (
                    <></>
                  )}
                </ul>
              </div>
            ))}
        </div>
      </div>

      <div className="footer-container">
        {/* Left Section: Company Info */}
        <div className="footer-company-info">
          <Link to="/">
            <img
              src={logo}
              alt="General Genomics Logo"
              width="120"
              className="footer-logo"
            />
          </Link>

          <p className="footer-description">
            General Genomics is a Service-Disabled Veteran Owned Business
            founded on the principle of harnessing data to heal the world.
          </p>
          <p>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </p>
        </div>

        {/* Right Section: Contact Info */}
        <div className="footer-contact-info footer-group">
          <div className="logos-container">
            <img src={sbacert} alt="Veteran Certified Logo" width="66" />
            <img src={sbagrowth} alt="Awardee Growth Logo" width="90" />
            <img
              style={isMobile ? { width: '120px', height: '130px' } : undefined}
              className="citi-logo"
              src={citi}
              alt="Citi Logo"
            />
          </div>
          <div className="contact-container">
            <h4 className="footer-contact-title">Contact Us</h4>
            <p className="footer-address">
              <Icon icon="location_on" />
              309 NW 13th St. STE 103 <br />
              Oklahoma City, OK 73103
            </p>
            <p className="footer-address">
              <Icon icon="location_on" />
              1412 W. Magnolia Avenue, <br /> Suite 300
              <br />
              Forth Worth, TX 76104
            </p>
            <p className="footer-phone">
              <Icon icon="phone" />
              <a href="tel:+14059146353">405.914.6353</a>
            </p>
            <p className="footer-email">
              <Icon icon="mail" />
              <a href="mailto:sales@ggenomics.com">sales@ggenomics.com</a>
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section: Social Media and Copyright */}
      <div className="footer-bottom">
        <div className="footer-bottom-container">
          <p className="footer-copyright">
            © Genomics {getFooterYear()}. All rights reserved.
          </p>
          <div className="footer-social-icons">
            <a
              href="https://www.facebook.com/generalgenomics"
              className="social-icon"
            >
              <img
                src={socialFacebook}
                alt="Facebook Logo"
                width="24"
                height="24"
              />
            </a>
            <a
              href="https://www.instagram.com/generalgenomics/"
              className="social-icon"
            >
              <img
                src={socialInstagram}
                alt="Instagram Logo"
                width="24"
                height="24"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/generalgenomics/"
              className="social-icon"
            >
              <img
                src={socialLinkedin}
                alt="LinkedIn Logo"
                width="24"
                height="24"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
